/**
 * Category → capability-class map. A `Record<ToolCategory, CapabilityClass>` so adding a
 * `ToolCategory` member without classifying it is a compile error.
 *
 *   - platform-operator: infrastructure / host / process / deploy / network / security /
 *     config. `db` is operator (db.query is infrastructure work; tenant entity access
 *     lives under content/atlas/pm).
 *   - enterprise-tenant: tenant-scoped product surface. `pm` deliberately: every pm table
 *     is tenant_id + FORCE RLS, so a tenant's backlog is its own data, not infrastructure.
 */
export const CATEGORY_CAPABILITY_CLASS = {
    shell: 'platform-operator',
    db: 'platform-operator',
    system: 'platform-operator',
    deployment: 'platform-operator',
    network: 'platform-operator',
    security: 'platform-operator',
    config: 'platform-operator',
    platform: 'platform-operator',
    release: 'platform-operator',
    content: 'enterprise-tenant',
    memory: 'enterprise-tenant',
    canvas: 'enterprise-tenant',
    atlas: 'enterprise-tenant',
    roadmap: 'enterprise-tenant',
    user: 'enterprise-tenant',
    files: 'enterprise-tenant',
    'ai-profiles': 'enterprise-tenant',
    navigation: 'enterprise-tenant',
    evaluation: 'enterprise-tenant',
    pm: 'enterprise-tenant',
};
/** A Map, not the object: a prototype key (`constructor`) must not answer the lookup. */
const CATEGORY_CLASS_LOOKUP = new Map(Object.entries(CATEGORY_CAPABILITY_CLASS));
/**
 * `ai_profiles.capability_class` column default (least privilege): every profile is
 * tenant-class unless an operator flips it. Pinned against the drizzle column by test.
 */
export const DEFAULT_PROFILE_CAPABILITY_CLASS = 'enterprise-tenant';
/** Permission flags that stand for a whole tool category (hard-gated at dispatch). */
export const PERMISSION_FLAG_CATEGORY = {
    allowShellAccess: 'shell',
    allowInternetAccess: 'network',
};
function isCapabilityClass(value) {
    return value === 'platform-operator' || value === 'enterprise-tenant';
}
/**
 * A tool's class — SINGLE SOURCE OF TRUTH; the dispatch guard and the offering filter
 * both call this. Explicit per-tool override, else the category map, else FAIL-CLOSED
 * to 'platform-operator' (unknown category, unknown override value, prototype key).
 */
export function resolveCapabilityClass(tool) {
    if (isCapabilityClass(tool.capabilityClass))
        return tool.capabilityClass;
    return CATEGORY_CLASS_LOOKUP.get(tool.category) ?? 'platform-operator';
}
/**
 * Offering visibility, mirroring the dispatch guard so a profile is never OFFERED a tool
 * it would be DENIED: operator profile → everything; tenant profile → tenant tools only.
 * Orthogonal to the whitelist / `alwaysAllowed` axis — callers AND this with those.
 */
export function isToolVisibleToProfileClass(tool, profileClass) {
    if (profileClass === 'platform-operator')
        return true;
    return resolveCapabilityClass(tool) === 'enterprise-tenant';
}
/**
 * A profile's class — SINGLE SOURCE OF TRUTH for profiles. A null/missing/unknown column
 * value → `undefined`, which every consumer treats as "not operator" (fail-closed).
 */
export function resolveProfileCapabilityClass(profile) {
    const c = profile?.capabilityClass;
    return isCapabilityClass(c) ? c : undefined;
}
function uniqueStrings(values) {
    const out = new Set();
    if (Array.isArray(values))
        for (const v of values)
            if (typeof v === 'string')
                out.add(v);
    return [...out];
}
/**
 * Grants that a profile of `profileClass` can never exercise (#2606): the offering filter
 * drops class-hidden tools BEFORE permissions are read, so such a grant is inert — the
 * toggle looks live and does nothing. Operator profiles have none.
 *
 * Category rule: with registered tools in the category, inert ⇔ every one is class-hidden
 * (a category holding a tenant-overridden tool, e.g. `platform`, is NOT inert); with none
 * registered, the category map decides; a category outside the map is an orphan — the
 * editor's orphan banner owns that disclosure, not this one. Deliberately asymmetric with the
 * tool rule (an unregistered NAME is always an orphan): a mapped category is a known surface
 * before any tool registers into it, a bare name is not; and since this layer only refuses a
 * NEW grant, an empty registry costs a spurious 400 on a mapped category, never a silent grant.
 */
export function findInertOperatorGrants(profileClass, permissions, tools) {
    if (profileClass === 'platform-operator' || !permissions)
        return { flags: [], categories: [], tools: [] };
    const perms = permissions;
    const byCategory = new Map();
    const byName = new Map();
    for (const tool of tools) {
        byName.set(tool.name, tool);
        const list = byCategory.get(tool.category) ?? [];
        list.push(tool);
        byCategory.set(tool.category, list);
    }
    const categoryIsInert = (category) => {
        const registered = byCategory.get(category);
        if (registered && registered.length > 0) {
            return registered.every((t) => !isToolVisibleToProfileClass(t, profileClass));
        }
        const mapped = CATEGORY_CLASS_LOOKUP.get(category);
        return mapped !== undefined && mapped !== 'enterprise-tenant';
    };
    const flags = Object.keys(PERMISSION_FLAG_CATEGORY).filter((flag) => perms[flag] === true && categoryIsInert(PERMISSION_FLAG_CATEGORY[flag]));
    const categories = uniqueStrings(perms.allowedCategories).filter(categoryIsInert);
    const inertTools = uniqueStrings(perms.allowedTools).filter((name) => {
        const tool = byName.get(name);
        return tool !== undefined && !isToolVisibleToProfileClass(tool, profileClass);
    });
    return { flags, categories, tools: inertTools };
}
//# sourceMappingURL=aiCapabilityClass.js.map